import { Injectable } from '@angular/core';

import { mergeMap, tap } from 'rxjs/operators';

import { Actions, ofType, createEffect } from '@ngrx/effects';

import { ScheduledApplicationsResolverActions, ApplicationsApiActions } from './scheduled-applications.actions';
import { ApplicationsConfigurationService } from '@era/settings-sdk';
import { VppApplicationsConfigurationService } from '@era/settings-sdk';
import { ScheduledApplicationsRequestService } from './scheduled-applications-request-service';
import { forkJoin } from 'rxjs';
import { safeMap } from '../effects.helpers';

@Injectable()
export class ScheduledApplicationsEffects {
  constructor(
    private readonly actions: Actions,
    private readonly applicationService: ApplicationsConfigurationService,
    private readonly vppApplicationsConfigurationService: VppApplicationsConfigurationService,
    private readonly scheduledApplicationsRequestService: ScheduledApplicationsRequestService
  ) {}

  /**
   * The (re)load of the scheduled applications has been requested.
   * Effect: we load the state from the api.
   */
  scheduledApplicationsRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ScheduledApplicationsResolverActions.scheduledApplicationsRequested),
      mergeMap(() =>
        safeMap(
          forkJoin({
            regular: this.applicationService.getAllScheduledApplications(),
            vpp: this.vppApplicationsConfigurationService.getAllScheduledVppApplications(),
          }),
          ({ regular, vpp }) =>
            ApplicationsApiActions.scheduledApplicationsLoaded({ applications: [...regular, ...vpp] }),
          (error) => ApplicationsApiActions.scheduledApplicationsLoadedError({ error })
        )
      )
    );
  });

  /**
   * The CSV file download of the scheduled applications has been requested.
   * Effect: we load the state from the api.
   */
  scheduledApplicationsCsvRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ScheduledApplicationsResolverActions.scheduledApplicationsCSVDownloadRequested),
      mergeMap(() =>
        safeMap(
          this.scheduledApplicationsRequestService.getAllScheduledApplicationsCsv().pipe(
            tap((res: Blob) => {
              this.createDownloadFile(res, 'text/csv', 'scheduled-applications-overview');
            })
          ),
          () => ApplicationsApiActions.scheduledApplicationsCSVDownloaded(),
          (error) => ApplicationsApiActions.scheduledApplicationsCSVDownloadedError({ error })
        )
      )
    );
  });

  private createDownloadFile(res: Blob, type: string, name: string) {
    // Create a new blob to add the file extension
    const blob = new Blob([res], { type: type });
    // Trigger the download
    const uri = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.download = name;
    anchor.href = uri;
    anchor.click();
  }
}
